/**
 * PendingInvitesDialog — owner / super-admin queue surface for the
 * approve-and-invite flow.
 *
 * Tabs (Feb 2026 update — lifecycle-explicit):
 *   • Pending   — pending_approval rows. Approve sends an invite. Reject
 *                 moves the row to the Rejected tab (reversible).
 *   • Invited   — invited rows (email sent, awaiting acceptance). Resend
 *                 / Revoke available.
 *   • Accepted  — accepted rows (recipient logged in). Read-only table.
 *   • Rejected  — rejected rows. Reopen (→ Pending) or Approve directly.
 *   • History   — terminal states (revoked / expired).
 *   • Add manually — owner-driven invite queue.
 *
 * Reject vs Revoke (design note):
 *   Reject is the "we said no during triage" decision and ONLY applies
 *   to pending rows. It's reversible. Revoke applies to already-sent
 *   invites where the email is out but the recipient hasn't accepted
 *   yet — it's terminal. The audit log distinguishes both.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from './ui/tabs';
import {
  Loader2, Mail, UserPlus, CheckCircle2, XCircle, RefreshCw, ShieldCheck, RotateCcw,
} from 'lucide-react';

const STATUS_PILL = {
  pending_approval: 'bg-amber-50 text-amber-800 border-amber-200',
  invited: 'bg-blue-50 text-blue-800 border-blue-200',
  accepted: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  revoked: 'bg-slate-100 text-slate-600 border-slate-200',
  expired: 'bg-rose-50 text-rose-700 border-rose-200',
};
const STATUS_LABEL = {
  pending_approval: 'Pending approval',
  invited: 'Invited — awaiting acceptance',
  accepted: 'Has Nova login',
  rejected: 'Rejected — reopenable',
  revoked: 'Revoked',
  expired: 'Expired',
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const PendingInvitesDialog = ({ open, onOpenChange, workspaceId, onCountChange }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // Manual invite form state
  const [manualEmail, setManualEmail] = useState('');
  const [manualName, setManualName] = useState('');
  const [manualSubmitting, setManualSubmitting] = useState(false);

  const fetchInvites = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await axios.get('/api/identity-invites');
      const list = r.data || [];
      setRows(list);
      // Surface pending+invited+rejected in the parent stat chip — rejected
      // counts because the owner usually wants to revisit those.
      const actionable = list.filter(
        (i) => ['pending_approval', 'invited', 'rejected'].includes(i.status)
      ).length;
      if (onCountChange) onCountChange(actionable);
    } catch (e) {
      toast.error('Failed to load invite queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspaceId]);

  const tabFilter = (r) => {
    if (activeTab === 'pending') return r.status === 'pending_approval';
    if (activeTab === 'invited') return r.status === 'invited';
    if (activeTab === 'accepted') return r.status === 'accepted';
    if (activeTab === 'rejected') return r.status === 'rejected';
    if (activeTab === 'history') return ['revoked', 'expired'].includes(r.status);
    return false;
  };
  const filtered = rows.filter(tabFilter);

  // Single action dispatcher; the server endpoint name == the action verb.
  const doAction = async (id, action) => {
    setBusyId(id);
    try {
      await axios.post(`/api/identity-invites/${id}/${action}`, {
        workspace_id: workspaceId,
      });
      const successMsg = {
        approve: 'Invite approved — email sent.',
        revoke: 'Invite revoked.',
        reject: 'Invite rejected. Reopen later from the Rejected tab.',
        reopen: 'Invite reopened — back to Pending.',
        resend: 'Invite re-sent.',
      }[action] || 'Done.';
      toast.success(successMsg);
      fetchInvites();
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.detail || e.message || 'Action failed';
      if (status === 503) {
        toast.error(
          typeof msg === 'string' && msg.toLowerCase().includes('email')
            ? `${msg} — open Super Admin → Email settings to add a Resend API key, then re-approve.`
            : msg,
          { duration: 12000 }
        );
      } else if (status === 502 && action === 'approve') {
        toast.error(
          typeof msg === 'string' ? msg : 'Approval saved, but the email send failed.',
          { duration: 10000 }
        );
        fetchInvites();
      } else {
        toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
    } finally {
      setBusyId(null);
    }
  };

  const submitManualInvite = async () => {
    const email = manualEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setManualSubmitting(true);
    try {
      await axios.post('/api/identity-invites/manual', {
        workspace_id: workspaceId,
        email,
        full_name: manualName.trim() || null,
        role: 'employee',
      });
      toast.success(`Queued ${email} — click Approve to send the invite email.`);
      setManualEmail('');
      setManualName('');
      setActiveTab('pending');
      fetchInvites();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not queue invite';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setManualSubmitting(false);
    }
  };

  const c = {
    pending: rows.filter((r) => r.status === 'pending_approval').length,
    invited: rows.filter((r) => r.status === 'invited').length,
    accepted: rows.filter((r) => r.status === 'accepted').length,
    rejected: rows.filter((r) => r.status === 'rejected').length,
    history: rows.filter((r) => ['revoked', 'expired'].includes(r.status)).length,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="pending-invites-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            Employee invites
          </DialogTitle>
          <DialogDescription>
            Approve who from your connected directory gets an Nova
            login. Passwordless sign-in only — recipient receives a magic
            link + 6-digit code (30-min expiry). Default role is{' '}
            <code className="bg-slate-100 px-1 py-0.5 rounded text-[11px]">employee</code> — you can elevate later
            from the People table.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="pending" data-testid="invites-tab-pending">
              Pending ({c.pending})
            </TabsTrigger>
            <TabsTrigger value="invited" data-testid="invites-tab-invited">
              Invited ({c.invited})
            </TabsTrigger>
            <TabsTrigger value="accepted" data-testid="invites-tab-accepted">
              Accepted ({c.accepted})
            </TabsTrigger>
            <TabsTrigger value="rejected" data-testid="invites-tab-rejected">
              Rejected ({c.rejected})
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="invites-tab-history">
              History ({c.history})
            </TabsTrigger>
            <TabsTrigger value="manual" data-testid="invites-tab-manual">
              <UserPlus className="w-3.5 h-3.5 mr-1" /> Add manually
            </TabsTrigger>
          </TabsList>

          {/* Lifecycle tabs all share the InviteList renderer; per-row
              actions are driven by the row.status so the same component
              naturally handles Reject (pending), Resend/Revoke (invited),
              and Reopen/Approve (rejected). Accepted gets its own
              denser table since the actions are read-only. */}
          <TabsContent value="pending" className="flex-1 overflow-y-auto mt-3">
            <InviteList rows={filtered} loading={loading} busyId={busyId} onAction={doAction} />
          </TabsContent>
          <TabsContent value="invited" className="flex-1 overflow-y-auto mt-3">
            <InviteList rows={filtered} loading={loading} busyId={busyId} onAction={doAction} />
          </TabsContent>
          <TabsContent value="accepted" className="flex-1 overflow-y-auto mt-3">
            <AcceptedTable rows={filtered} loading={loading} />
          </TabsContent>
          <TabsContent value="rejected" className="flex-1 overflow-y-auto mt-3">
            <InviteList rows={filtered} loading={loading} busyId={busyId} onAction={doAction} />
          </TabsContent>
          <TabsContent value="history" className="flex-1 overflow-y-auto mt-3">
            <InviteList rows={filtered} loading={loading} busyId={busyId} onAction={doAction} />
          </TabsContent>
          <TabsContent value="manual" className="flex-1 overflow-y-auto mt-3 space-y-3">
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Manual invites bypass the auto-eligibility gate but still go through
              the approve-and-send flow — the queue ends up on the Pending tab.
            </div>
            <div>
              <Label htmlFor="manual-invite-email" className="text-xs">Email *</Label>
              <Input
                id="manual-invite-email"
                type="email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                placeholder="person@yourcompany.com"
                className="mt-1"
                data-testid="manual-invite-email-input"
              />
            </div>
            <div>
              <Label htmlFor="manual-invite-name" className="text-xs">Full name (optional)</Label>
              <Input
                id="manual-invite-name"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                placeholder="Jane Doe"
                className="mt-1"
                data-testid="manual-invite-name-input"
              />
            </div>
            <Button
              onClick={submitManualInvite}
              disabled={manualSubmitting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="manual-invite-submit"
            >
              {manualSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Queue invite
            </Button>
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t border-slate-200 pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pending-invites-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const InviteList = ({ rows, loading, busyId, onAction }) => {
  if (loading) {
    return (
      <div className="text-sm text-slate-500 flex items-center gap-2 py-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (rows.length === 0) {
    return <div className="text-sm text-slate-500 py-8 text-center">Nothing here.</div>;
  }
  return (
    <div className="space-y-2" data-testid="invite-list">
      {rows.map((r) => (
        <div
          key={r.id}
          className="rounded-lg border border-slate-200 px-3 py-2.5 flex items-center justify-between gap-3"
          data-testid={`invite-row-${r.id}`}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="font-medium text-slate-900 truncate">{r.email}</span>
              <Badge variant="outline" className={STATUS_PILL[r.status] || ''}>
                {STATUS_LABEL[r.status] || r.status}
              </Badge>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 pl-5 flex items-center gap-2 flex-wrap">
              {r.full_name && <span>{r.full_name} · </span>}
              <span>role: {r.role}</span>
              <span>· source: {r.source}</span>
              {r.invited_at && (
                <span>· invited {new Date(r.invited_at).toLocaleDateString()}</span>
              )}
              {r.status === 'rejected' && r.rejected_at && (
                <span>· rejected {new Date(r.rejected_at).toLocaleDateString()}</span>
              )}
              {r.status === 'rejected' && r.rejection_reason && (
                <span className="italic">· reason: {r.rejection_reason}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {r.status === 'pending_approval' && (
              <>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={busyId === r.id}
                  onClick={() => onAction(r.id, 'approve')}
                  data-testid={`invite-approve-${r.id}`}
                >
                  {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</>}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === r.id}
                  onClick={() => onAction(r.id, 'reject')}
                  data-testid={`invite-reject-${r.id}`}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                </Button>
              </>
            )}
            {r.status === 'invited' && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === r.id}
                  onClick={() => onAction(r.id, 'resend')}
                  data-testid={`invite-resend-${r.id}`}
                >
                  {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Resend</>}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === r.id}
                  onClick={() => onAction(r.id, 'revoke')}
                  data-testid={`invite-revoke-${r.id}`}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Revoke
                </Button>
              </>
            )}
            {r.status === 'rejected' && (
              <>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={busyId === r.id}
                  onClick={() => onAction(r.id, 'approve')}
                  data-testid={`invite-approve-from-rejected-${r.id}`}
                  title="Approve directly from rejected state — sends the invite email"
                >
                  {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</>}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === r.id}
                  onClick={() => onAction(r.id, 'reopen')}
                  data-testid={`invite-reopen-${r.id}`}
                  title="Move back to Pending without sending the invite yet"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen
                </Button>
              </>
            )}
            {/* History rows (revoked/expired) — no actions, terminal state. */}
          </div>
        </div>
      ))}
    </div>
  );
};

// Compact read-only table for users who completed acceptance.
// Designed to surface the audit-relevant facts at a glance: who, when,
// from where, and whether their access is still active.
const AcceptedTable = ({ rows, loading }) => {
  if (loading) {
    return (
      <div className="text-sm text-slate-500 flex items-center gap-2 py-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-8 text-center" data-testid="accepted-empty">
        No accepted invites yet.
      </div>
    );
  }
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="accepted-table">
      <table className="w-full text-[12px]">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-left px-3 py-1.5 font-medium">Email</th>
            <th className="text-left px-3 py-1.5 font-medium">Name</th>
            <th className="text-left px-3 py-1.5 font-medium">Accepted</th>
            <th className="text-left px-3 py-1.5 font-medium">Role</th>
            <th className="text-left px-3 py-1.5 font-medium">Source</th>
            <th className="text-left px-3 py-1.5 font-medium">Access</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50" data-testid={`accepted-row-${r.id}`}>
              <td className="px-3 py-1.5 text-slate-900">{r.email}</td>
              <td className="px-3 py-1.5 text-slate-700">{r.full_name || '—'}</td>
              <td className="px-3 py-1.5 text-slate-600 tabular-nums">{fmtDateTime(r.accepted_at)}</td>
              <td className="px-3 py-1.5">
                <Badge variant="outline" className="bg-slate-50 text-slate-700 text-[10px] px-1.5 py-0">
                  {r.role || 'employee'}
                </Badge>
              </td>
              <td className="px-3 py-1.5 text-slate-500">{r.source || '—'}</td>
              <td className="px-3 py-1.5">
                {r.user_is_suspended ? (
                  <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px] px-1.5 py-0">
                    Suspended
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px] px-1.5 py-0">
                    Active
                  </Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PendingInvitesDialog;
